import { DataEditor, GridCell, GridCellKind, GridColumn, Item } from '@glideapps/glide-data-grid';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ExcelData,
  LiquidacionAsincronaEstado,
  LiquidacionAsincronaV2Details,
} from '../../../../../system/liquidacion_asincrona/LiquidacionAsincronaV2System';
import { DefaultPalette, PrimaryButton, Spinner } from '@fluentui/react';
import { t } from 'i18next';
import TimeTaxSupportSystemClient from '../../../../../system';
import MessageCourier from '../../../../../lib/MessageCourier';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../../common/constants';

interface SettlementTablePreliquidadoAjeProps {
  settlement: LiquidacionAsincronaV2Details;
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (v: boolean) => void;
  id: string;
}

interface ColumnConfig {
  title: string;
  editable?: boolean;
  width?: number;
}
/* 
[
    "UUID",
    "EJERCICIO",
    "PERIODO",
    "TIPO_DE_INFORMACION",
    "COMPANIA",
    "COMPANIA_",
    "CONCEPTO",
    "DETALLE",
    "DETALLE_",
    "Doc_Identidad",
    "Persona",
    "CostoXHora",
    "HORAS",
    "IMPORTE_DE_HRS_SIN_MARGEN",
    "IMPORTE_DE_HRS_CON_MARGEN",
    "IMPORTE_DE_GASTOS_SIN_MARGEN",
    "IMPORTE_DE_GASTOS_CON_MARGEN",
    "IMPORTE_TOTAL_SIN_MARGEN",
    "IMPORTE_TOTAL_CON_MARGEN",
    "MARGEN_APLICADO",
    "MARGEN_APLICADO_PORCENTAJE",
    "DETALLE_COMPANIA_CANONICO_BINDING",
    "DETALLE_COMPANIA_CANONICO_EMBEDING",
    "DETALLE_COMPANIA_EMBEDING"
  ],
 */
const columnConfig: Record<string, ColumnConfig> = {
  EJERCICIO: { title: 'Ejercicio' },
  PERIODO: { title: 'Periodo' },
  COMPANIA: { title: 'Compañía (*)', editable: true },
  COMPANIA_: { title: 'Compañía_' },
  CONCEPTO: { title: 'Concepto' },

  DETALLE: { title: 'Detalle (*)', editable: true },
  DETALLE_: { title: 'Detalle_' },

  Doc_Identidad: { title: 'Doc. Identidad' },
  Persona: { title: 'Persona' },

  CostoXHora: { title: 'Costo x Hora' },

  HORAS: { title: 'Horas' },

  IMPORTE_DE_HRS_SIN_MARGEN: { title: 'Importe horas sin margen' },
  IMPORTE_DE_HRS_CON_MARGEN: { title: 'Importe horas con margen' },

  IMPORTE_DE_GASTOS_SIN_MARGEN: { title: 'Importe gastos sin margen' },
  IMPORTE_DE_GASTOS_CON_MARGEN: { title: 'Importe gastos con margen' },

  IMPORTE_TOTAL_SIN_MARGEN: { title: 'Importe total sin margen' },
  IMPORTE_TOTAL_CON_MARGEN: { title: 'Importe total con margen' },

  MARGEN_APLICADO_PORCENTAJE: { title: 'Margen aplicado (%)' },

  DETALLE_COMPANIA_CANONICO_BINDING: { title: 'Detalle compañía canónico binding', width: 300 },
  DETALLE_COMPANIA_CANONICO_EMBEDING: { title: 'Detalle compañía canónico embedding', width: 300 },
  DETALLE_COMPANIA_EMBEDING: { title: 'Detalle compañía embedding', width: 300 },
};

export default function SettlementTablePreliquidadoAje({ id, system, courier, settlement, changeBlockUI }: SettlementTablePreliquidadoAjeProps) {
  const [loading, setLoading] = useState(true);

  const [rows, setRows] = useState<ExcelData['rows']>([]);
  const [importeTotal, setImporteTotal] = useState<string>('');

  const columnKeys = useMemo(() => Object.keys(columnConfig), []);

  const columns: GridColumn[] = columnKeys.map((key) => ({
    title: columnConfig[key].title,
    width: columnConfig[key].width ?? 180,
  }));

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Si la liquidación está en estado PENDIENTE, obtenemos el reporte de revisión para mostrarlo en la tabla
        if (settlement.liquidacion.lidasinc_estado === LiquidacionAsincronaEstado.PENDIENTE) {
          const reporteRevision = await system.getLiquidacionAsincronaV2System().getReporteRevision(id);
          if (reporteRevision) {
            // Filtramos solo las filas normales (sin la fila de totales) para mostrarlas en la tabla
            setRows(reporteRevision.rows.filter((r) => r.UUID !== 'TOTALES'));

            // Obtenemos el importe total del reporte de revisión para mostrarlo en el detalle y usarlo al reprocesar la liquidación
            const totalesRow = reporteRevision.rows.find((r) => r.UUID === 'TOTALES');
            if (totalesRow) {
              setImporteTotal(totalesRow['IMPORTE_TOTAL_CON_MARGEN']);
            }
          } else {
            courier.messageError(t('No se pudo cargar el reporte de revisión'));
          }
        }
      } catch (error) {
        console.log(error);
        courier.messageError(t('Error al cargar el reporte de revisión'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, settlement]);

  const editableColumns = columnKeys.map((key, idx) => (columnConfig[key].editable ? idx : -1)).filter((idx) => idx !== -1);

  // Obtener contenido de cada celda
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const colName = columnKeys[col];

      const value = rows[row][colName] ?? '';

      const isEditable = editableColumns.includes(col);
      return {
        kind: GridCellKind.Text,
        data: value,
        displayData: value,
        readonly: !isEditable,
        allowOverlay: isEditable,
      };
    },
    [rows, editableColumns, columnKeys],
  );

  // Manejo de edición
  const onCellEdited = useCallback(
    ([col, row]: Item, newValue: GridCell) => {
      if (newValue.kind !== GridCellKind.Text) return;

      setRows((prev) => {
        const updated = [...prev];
        const colName = columnKeys[col];
        updated[row] = { ...updated[row], [colName]: newValue.data };
        return updated;
      });
    },
    [columnKeys],
  );

  // Validación para habilitar/deshabilitar el botón Reprocesar
  const isReprocesarEnabled = useMemo(() => {
    return rows.every((row) => {
      for (const colKey of columnKeys) {
        if (columnConfig[colKey].editable) {
          const value = String(row[colKey] ?? '').trim();

          if (!value /* || (colKey === 'DETALLE' && value.toUpperCase() === 'REVISAR') */) {
            return false;
          }
        }
      }
      return true;
    });
  }, [rows, columnKeys]);

  const handleReprocesar = async () => {
    const payload = rows.map((r) => ({
      UUID: r.UUID,
      COMPANIA: r.COMPANIA,
      DETALLE: r.DETALLE,
    }));

    try {
      changeBlockUI(true);
      await system.getLiquidacionAsincronaV2System().actualizarReporte(id, payload, importeTotal);
      courier.messageSuccess(t('Reporte actualizado correctamente'));
    } catch (error) {
      console.error('Error al actualizar reporte:', error);
      courier.messageError(t('Error al actualizar el reporte'));
    } finally {
      changeBlockUI(false);
      navigate(routes.direct_settlement_list_aje);
    }
  };

  if (loading) {
    return <Spinner label={t('Cargando reporte...')} />;
  }

  if (rows.length === 0) {
    return <div>{t('No hay datos para mostrar')}</div>;
  }

  return (
    <>
      {/* FOOTER */}
      <div className="detail-footer-aje">
        <PrimaryButton
          text={t('Reprocesar')}
          iconProps={{ iconName: 'Refresh', color: DefaultPalette.themeLighterAlt }}
          onClick={handleReprocesar}
          disabled={!isReprocesarEnabled}
        />
      </div>

      <div style={{ height: '600px' }}>
        <DataEditor
          columns={columns}
          rows={rows.length}
          getCellContent={getCellContent}
          onCellEdited={onCellEdited}
          rowMarkers="none"
          columnSelect="none"
          smoothScrollX
          smoothScrollY
        />
      </div>
    </>
  );
}
